import { ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { ApiOptions, User } from '@csp/csp-common-model';
import { findScheduleVersionsActiveNow } from '@csp/csp-common-scheduling';
import { toRestOptions } from '@csp/csp-fe-auth';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import { RoleType } from '@csp/dmdp-api-common-dto';
import {
  ConsentApprovalCriteriaFieldV1,
  ConsentApprovalQueryOperatorV1,
  ConsentApprovalQuerySortOrderV1,
  ConsentApprovalQueryTypeV1,
  ConsentApprovalQueryV1,
  ConsentApprovalSortFieldV1,
  ConsentApprovalV1,
} from '@csp/dmdp-api-consent-dto';
import { isEmpty } from 'lodash';
import { ConsentFeatureTypeV1, ConsentFormV1 } from '@csp/dto';
import { isDefined } from '@csp/csp-common-util';
import { AnsweredConsentRequest, AnsweredLegacyConsent, SkippedConsent } from '../model/AnsweredConsent';
import { ConsentPlatform } from '../model/ConsentPlatform';
import { ConsentRequest } from '../model/ConsentRequest';
import { ConsentSchedule } from '../model/ConsentSchedule';
import { ConsentVersion } from '../model/ConsentVersion';

const filterActiveConsentRequestsByRoleAndPlatform = (
  activeSchedules: ConsentSchedule[],
  role: RoleType,
  platform: ConsentPlatform,
): ConsentRequest[] => {
  const activeVersionWithinActiveWindow: ConsentVersion[] = findScheduleVersionsActiveNow(
    activeSchedules,
    ZonedDateTimeFactory.now(),
  );

  return activeVersionWithinActiveWindow
    .flatMap((v: ConsentVersion) => v.requests)
    .filter((request: ConsentRequest) => request.meta.roles.includes(role) && request.meta.platform === platform);
};

const filterNoneApprovedRequests = async (
  requests: ConsentRequest[],
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<ConsentRequest[]> => {
  if (isEmpty(requests)) {
    return [];
  }

  const artifactCodes = requests
    .map(request => request.details?.artifactCode)
    .filter(artifactCode => artifactCode) as string[];

  const uniqueArtifactCodes = [...new Set(artifactCodes)];
  const approvals = await fetchConsentApprovalsByUser(uniqueArtifactCodes, user, apiOptions);
  const approvalsArtifactCodes = approvals.map(approval => approval.artifactCode);

  return requests.filter(
    request => request.details?.artifactCode && !approvalsArtifactCodes.includes(request.details.artifactCode),
  );
};

const filterApprovedRequests = async (
  requests: ConsentRequest[],
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<ConsentRequest[]> => {
  if (isEmpty(requests)) {
    return [];
  }

  const artifactCodes = requests
    .map(request => request.details?.artifactCode)
    .filter(artifactCode => artifactCode) as string[];

  const uniqueArtifactCodes = [...new Set(artifactCodes)];
  const approvals = await fetchConsentApprovalsByUser(uniqueArtifactCodes, user, apiOptions);
  const approvalsArtifactCodes = approvals.map(approval => approval.artifactCode);

  // Filter out requests that have no approval/consent on them, i.e. pending consents
  return requests.filter(
    request => request.details?.artifactCode && approvalsArtifactCodes.includes(request.details.artifactCode),
  );
};

const filterActiveNoneApprovedRequests = async (
  activeSchedules: ConsentSchedule[],
  role: RoleType,
  platform: ConsentPlatform,
  user: User,
  onFeatures?: ConsentFeatureTypeV1[],
  apiOptions?: ApiOptions,
): Promise<ConsentRequest[]> => {
  let requests: ConsentRequest[] = filterActiveConsentRequestsByRoleAndPlatform(activeSchedules, role, platform);

  if (onFeatures && onFeatures.length > 0) {
    requests = requests.filter(request =>
      request.meta.onFeature.some(feature => onFeatures.includes(feature.type as unknown as ConsentFeatureTypeV1)),
    );
  }

  return await filterNoneApprovedRequests(requests, user, apiOptions);
};

const filterActiveApprovedRequests = async (
  activeSchedules: ConsentSchedule[],
  role: RoleType,
  platform: ConsentPlatform,
  user: User,
  apiOptions?: ApiOptions,
): Promise<ConsentRequest[]> => {
  const requests: ConsentRequest[] = filterActiveConsentRequestsByRoleAndPlatform(activeSchedules, role, platform);
  return await filterApprovedRequests(requests, user, apiOptions);
};

/**
 * Map requests with their most recent "approval" object and optional "skippedAt" timestamp (from graphql input)
 * which are needed to determine the consent status: APPROVED or DECLINED
 */
const filterAnsweredRequests = async (
  requests: ConsentRequest[],
  skippedConsents: SkippedConsent[] = [],
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<AnsweredConsentRequest[]> => {
  if (isEmpty(requests)) {
    return [];
  }

  const artifactCodes = requests.map(request => request.details?.artifactCode).filter(isDefined);
  const uniqueArtifactCodes = [...new Set(artifactCodes)];
  const approvals = await fetchConsentApprovalsByUserSortedTime(uniqueArtifactCodes, user, apiOptions);

  return requests
    .map(request => {
      const skippedConsent = skippedConsents.find(
        skippedConsent => skippedConsent.artifactCode === request.details?.artifactCode,
      );

      const mostRecentApproval = approvals.find(a => a.artifactCode === request.details?.artifactCode) ?? null;

      return mostRecentApproval || skippedConsent
        ? {
            ...request,
            approval: mostRecentApproval,
            skippedAt: skippedConsent?.skippedAt ?? null,
          }
        : undefined;
    })
    .filter(isDefined);
};

/**
 * Map legacy consents with their most recent "approval" object
 * which is needed to determine the consent status: APPROVED or DECLINED
 */
const filterAnsweredLegacyConsents = async (
  legacyConsents: Array<ConsentFormV1>,
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<AnsweredLegacyConsent[]> => {
  const artifactsCodes = legacyConsents.map(legacyConsent => legacyConsent.artifact.artifactCode);
  const uniqueArtifactCodes = [...new Set(artifactsCodes)];
  const approvals = await fetchConsentApprovalsByUser(uniqueArtifactCodes, user, apiOptions);

  return legacyConsents.map<AnsweredLegacyConsent>(legacyConsent => {
    const approval = approvals.find(approval => approval.artifactCode === legacyConsent.artifact.artifactCode) ?? null;

    return {
      ...legacyConsent,
      approval,
    };
  });
};

const approvalsByMatchingUserAndArtifactCodesQuery = (
  artifactCodes: string[],
  userId: string,
): ConsentApprovalQueryV1 => ({
  criterion: {
    operator: ConsentApprovalQueryOperatorV1.AND,
    criteria: [
      {
        fieldCriterion: {
          field: ConsentApprovalCriteriaFieldV1.ARTIFACT_CODE,
          type: ConsentApprovalQueryTypeV1.IN,
          values: artifactCodes,
        },
      },
      {
        fieldCriterion: {
          field: ConsentApprovalCriteriaFieldV1.USER_ID,
          type: ConsentApprovalQueryTypeV1.IN,
          values: [userId],
        },
      },
    ],
  },
});

const fetchConsentApprovalsByUser = async (
  artifactCodes: string[],
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<ConsentApprovalV1[]> => {
  if (isEmpty(artifactCodes)) {
    return [];
  }

  const query: ConsentApprovalQueryV1 = approvalsByMatchingUserAndArtifactCodesQuery(artifactCodes, user.userId);
  const { approvals } = await ConsentRestServiceV1.queryApprovalsV1(toRestOptions(apiOptions), query);

  return approvals;
};

const fetchConsentApprovalsByUserSortedTime = async (
  artifactCodes: string[],
  user: Pick<User, 'userId'>,
  apiOptions?: ApiOptions,
): Promise<ConsentApprovalV1[]> => {
  if (isEmpty(artifactCodes)) {
    return [];
  }

  const query: ConsentApprovalQueryV1 = {
    ...approvalsByMatchingUserAndArtifactCodesQuery(artifactCodes, user.userId),
    sort: [
      {
        field: ConsentApprovalSortFieldV1.APPROVAL_TIMESTAMP,
        order: ConsentApprovalQuerySortOrderV1.DESC,
      },
    ],
  };
  const { approvals } = await ConsentRestServiceV1.queryApprovalsV1(toRestOptions(apiOptions), query);

  return approvals;
};

export const consentFilterUtil = {
  filterActiveConsentRequestsByRoleAndPlatform,
  filterActiveNoneApprovedRequests,
  filterNoneApprovedRequests,
  filterActiveApprovedRequests,
  filterApprovedRequests,
  filterAnsweredRequests,
  filterAnsweredLegacyConsents,
};
